import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/axios'
import type { NewsEvent, NewsEventCreate, ImportResult } from '@/types/news'

export const useNewsEvents = () => {
  const queryClient = useQueryClient()

  const eventsQuery = useQuery({
    queryKey: ['news-events'],
    queryFn: async () => {
      const { data } = await api.get<NewsEvent[]>('/news/events')
      return data
    },
  })

  const createEventMutation = useMutation({
    mutationFn: async (eventData: NewsEventCreate) => {
      const { data } = await api.post<NewsEvent>('/news/events', eventData)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-events'] })
    },
  })

  const updateEventMutation = useMutation({
    mutationFn: async ({ id, data: eventData }: { id: number; data: NewsEventCreate }) => {
      const { data } = await api.put<NewsEvent>(`/news/events/${id}`, eventData)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-events'] })
    },
  })

  const deleteEventMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/news/events/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-events'] })
    },
  })

  const importJsonMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post<ImportResult>('/news/import-json', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-events'] })
    },
  })

  const fetchExternalMutation = useMutation({
    mutationFn: async () => {
      const { data} = await api.post<ImportResult>('/news/fetch-external')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-events'] })
    },
  })

  return {
    events: eventsQuery.data || [],
    isLoading: eventsQuery.isLoading,
    isError: eventsQuery.isError,
    error: eventsQuery.error,
    
    createEvent: createEventMutation.mutateAsync,
    isCreating: createEventMutation.isPending,
    
    updateEvent: updateEventMutation.mutateAsync,
    isUpdating: updateEventMutation.isPending,
    
    deleteEvent: deleteEventMutation.mutateAsync,
    isDeleting: deleteEventMutation.isPending,
    
    importJson: importJsonMutation.mutateAsync,
    isImporting: importJsonMutation.isPending,
    
    fetchExternal: fetchExternalMutation.mutateAsync,
    isFetching: fetchExternalMutation.isPending,
  }
}
